import { useState, useCallback, useEffect, useRef } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetRecentExpenseActivityUseCase } from "../../domain/use-cases/GetRecentExpenseActivityUseCase";
import type { GetRecentExpenseActivityParams } from "../../domain/repositories/ClientOverviewRepository";
import type { RecentExpenseActivityItemEntity } from "../../domain/entities/RecentExpenseActivityEntity";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";

const repository = new ClientOverviewRepositoryImpl();
const getRecentExpenseActivityUseCase = new GetRecentExpenseActivityUseCase(repository);

const ITEMS_PER_PAGE = 10;

export function useRecentExpenseActivity() {
  useExpenseBootstrap();

  const [period, setPeriod] = useState<GetRecentExpenseActivityParams["period"]>("last30d");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [filter, setFilter] = useState<GetRecentExpenseActivityParams["status"]>("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<RecentExpenseActivityItemEntity[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const skipNextFetchRef = useRef(false);

  const fetchActivity = useCallback(
    async (
      pageNum: number,
      overrides?: {
        period?: GetRecentExpenseActivityParams["period"];
        filter?: GetRecentExpenseActivityParams["status"];
        date_from?: string;
        date_to?: string;
      },
    ): Promise<void> => {
      setLoading(true);
      setError(null);
      const p = overrides?.period ?? period;
      const f = overrides?.filter ?? filter;
      const from = overrides?.date_from ?? customStartDate;
      const to = overrides?.date_to ?? customEndDate;

      const params: GetRecentExpenseActivityParams = {
        status: f,
        page: pageNum,
        limit: ITEMS_PER_PAGE,
      };
      if (p === "custom" && from && to) {
        params.period = "custom";
        params.date_from = from;
        params.date_to = to;
      } else {
        params.period = p;
      }

      try {
        const entity = await getRecentExpenseActivityUseCase.execute(params);
        setRows(entity.items);
        setTotalItems(entity.pagination.total);
        setTotalPages(entity.pagination.totalPages);
        setPage(entity.pagination.page);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load activity");
        setRows([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [period, filter, customStartDate, customEndDate],
  );

  const tryHydrateFromBootstrap = useCallback(async (): Promise<boolean> => {
    let bootstrapEntity = getExpenseBootstrapData();
    if (!bootstrapEntity) {
      const bootstrapPromise = getExpenseBootstrapPromise();
      if (bootstrapPromise) {
        try {
          bootstrapEntity = await bootstrapPromise;
        } catch {
          return false;
        }
      }
    }
    if (!bootstrapEntity) {
      return false;
    }
    const section = bootstrapEntity.sections.recentActivity;
    if (section.status !== "ok" || !section.data) {
      return false;
    }
    const entity = section.data;
    setRows(entity.items);
    setTotalItems(entity.pagination.total);
    setTotalPages(entity.pagination.totalPages);
    setPage(entity.pagination.page);
    setError(null);
    return true;
  }, []);

  useEffect(() => {
    if (period === "custom") {
      return;
    }
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    void (async () => {
      setLoading(true);
      setError(null);

      if (period === "last30d" && filter === "all") {
        const ok = await tryHydrateFromBootstrap();
        if (ok) {
          setLoading(false);
          return;
        }
      }

      await fetchActivity(1);
    })();
  }, [period, filter, fetchActivity, tryHydrateFromBootstrap]);

  const handlePeriodChange = useCallback((newPeriod: GetRecentExpenseActivityParams["period"]) => {
    setPeriod(newPeriod);
    setPage(1);
    if (newPeriod === "custom") {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  }, []);

  const handleFilterChange = useCallback((newFilter: GetRecentExpenseActivityParams["status"]) => {
    setFilter(newFilter);
    setPage(1);
  }, []);

  const handleCustomApply = useCallback(
    (start: string, end: string) => {
      setCustomStartDate(start);
      setCustomEndDate(end);
      setShowCustomRange(false);
      setPage(1);
      void fetchActivity(1, { period: "custom", date_from: start, date_to: end, filter });
    },
    [fetchActivity, filter],
  );

  const handleCustomRangeCancel = useCallback(() => {
    skipNextFetchRef.current = true;
    setPeriod("last30d");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomRange(false);
    setPage(1);
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      void fetchActivity(newPage);
    },
    [fetchActivity],
  );

  return {
    period,
    showCustomRange,
    customStartDate,
    customEndDate,
    filter,
    rows,
    loading,
    error,
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: ITEMS_PER_PAGE,
    onPeriodChange: handlePeriodChange,
    onCustomStartDateChange: setCustomStartDate,
    onCustomEndDateChange: setCustomEndDate,
    onCustomApply: handleCustomApply,
    onCustomRangeCancel: handleCustomRangeCancel,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    refetch: () => fetchActivity(page),
  };
}
